import type { MouseEvent, ReactNode } from 'react';
import { useId } from 'react';
import styled from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { SvgIcon } from '../icons/SvgIcon';
import { Body } from '../typography/Body';
import { H3 } from '../typography/HeadingLevels';

export type OptionCardGroupProps = {
  /** Visible group heading (Figma "Erstattungsprozess"); labels the fieldset through `aria-labelledby`. */
  legend: string;
  /** Muted line under the legend (Figma "Wähle deinen Erstattungsprozess"). */
  hint?: string;
  children: ReactNode;
};

const GroupRoot = styled.fieldset`
  margin: 0;
  border: 1px solid ${getMediumRGBA(0.1)};
  border-radius: ${radii.lg};
  padding: ${space.md};
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

export function OptionCardGroup({ legend, hint, children }: OptionCardGroupProps) {
  const hasHint = hint != null && hint !== '';
  const legendId = useId();
  return (
    <GroupRoot aria-labelledby={legendId}>
      <H3 id={legendId}>{legend}</H3>
      {hasHint ? (
        <Body variant="document" tone="muted">
          {hint}
        </Body>
      ) : null}
      <CardStack>{children}</CardStack>
    </GroupRoot>
  );
}

export type OptionCardSectionLabelProps = { children: string; badge?: string };

const SectionLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.xs};
`;

const SectionLabelText = styled.span`
  text-transform: uppercase;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.mini.fontSize};
  line-height: ${text.mini.lineHeight};
  font-weight: ${text.mini.fontWeight};
  color: ${colors.medium};
`;

/** Figma literals; the palette has no badge green. */
const SectionLabelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.mini.fontSize};
  line-height: ${text.mini.lineHeight};
  font-weight: 500;
  color: #2d8a34;
  background: #eaf6ec;
  border-radius: ${radii.control};
  padding: 4px 8px;
`;

export function OptionCardSectionLabel({ children, badge }: OptionCardSectionLabelProps) {
  const hasBadge = badge != null && badge !== '';
  return (
    <SectionLabelRow>
      <SectionLabelText>{children}</SectionLabelText>
      {hasBadge ? <SectionLabelBadge>{badge}</SectionLabelBadge> : null}
    </SectionLabelRow>
  );
}

export type OptionCardProps = {
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  /** Head line next to the radio glyph; the frames put the emoji inside the text. */
  title: ReactNode;
  description: ReactNode;
  /** Extra content shown only while checked (trigger, inline timeline, support hint). */
  children?: ReactNode;
};

const Root = styled.div<{ $checked: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
  padding: ${space.md};
  border-radius: ${radii.lg};
  border: 2px solid ${({ $checked }) => ($checked ? colors.primary : getMediumRGBA(0.1))};
  background: ${({ $checked }) =>
    $checked ? `color-mix(in srgb, ${colors.primary} 8%, ${colors.white})` : colors.white};
  cursor: pointer;
`;

const HeadRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${space.sm};
`;

const RadioHitArea = styled.label`
  display: inline-flex;
  flex-shrink: 0;
  cursor: pointer;
`;

const NativeRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const RadioGlyph = styled.span`
  display: inline-flex;
  border-radius: ${radii.pill};

  input:focus-visible + & {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

const TitleText = styled.span`
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.body.fontSize};
  line-height: ${text.body.lineHeight};
  font-weight: 500;
  color: ${colors.dark};
`;

const ChildrenStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.xs};
`;

/** Ignores clicks on interactive descendants so links/buttons in `children` keep their own behaviour (`Checkbox.tsx` pattern). */
function isActivatableLinkTarget(target: HTMLElement) {
  return Boolean(target.closest('a[href], button, [role="link"], [role="button"]'));
}

export function OptionCard({ name, value, checked, onSelect, title, description, children }: OptionCardProps) {
  const autoId = useId();
  const inputId = `${autoId}-input`;
  const titleId = `${autoId}-title`;
  const descriptionId = `${autoId}-description`;

  const handleRootClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (isActivatableLinkTarget(target) || target.closest('label')) {
      return;
    }
    onSelect(value);
  };

  return (
    <Root $checked={checked} onClick={handleRootClick}>
      <HeadRow>
        <RadioHitArea htmlFor={inputId}>
          <NativeRadio
            id={inputId}
            type="radio"
            name={name}
            value={value}
            checked={checked}
            onChange={() => onSelect(value)}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
          />
          <RadioGlyph aria-hidden>
            <SvgIcon name={checked ? 'radioselected' : 'radiounselected'} size={20} />
          </RadioGlyph>
        </RadioHitArea>
        <TitleText id={titleId}>{title}</TitleText>
      </HeadRow>
      <Body id={descriptionId} variant="document" tone="muted">
        {description}
      </Body>
      {checked && children ? <ChildrenStack>{children}</ChildrenStack> : null}
    </Root>
  );
}
